import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { Link, generatePath } from "react-router-dom";
import { ArrowRight, History } from "lucide-react";

import { UIButton, UICard, UIChip } from "../../ui";
import TruncatedWithTooltip from "../../TruncatedWithTooltip";
import { clx } from "../../../utils/Helpers";
import { ROUTE_PATHS } from "../../../utils/RoutesPaths";
import DashboardEmptyState from "../DashboardEmptyState";
import {
  ACTIVITY_MODULE_COLOR,
  RECENT_ACTIVITY_LIMIT,
  formatActivityDateTime,
} from "./constants";
import "../dashboardSection.scss";
import "./RecentActivityLog.scss";

/**
 * Dashboard "Recent Activity Log" (admin + company portals) — the `limit` most
 * recent entries, newest first, one row each: date/time, the actor with their
 * role underneath, the company (admin portal, `showCompany`; "—" for a
 * platform-level entry), the action, and the module tag. `visibleRows` rows
 * show before the list scrolls. Entries are read-only. `modules` maps a module
 * id to its tag label; the tag colour comes from `ACTIVITY_MODULE_COLOR`.
 */
const RecentActivityLog = ({
  title,
  items,
  modules,
  lang,
  limit,
  visibleRows,
  showCompany,
  fullLogsLabel,
  onFullLogs,
  emptyTitle,
  emptyDescription,
}) => {
  const entries = useMemo(
    () =>
      [...items]
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
        .slice(0, limit),
    [items, limit],
  );

  // No activity → no "Full Logs": it would only open an empty page.
  const action = fullLogsLabel && entries.length > 0 ? (
    <UIButton
      variant="text"
      className="dashboard-link"
      endIcon={<ArrowRight size={16} strokeWidth={2} />}
      onClick={onFullLogs}
    >
      {fullLogsLabel}
    </UIButton>
  ) : undefined;

  return (
    <UICard
      title={title}
      action={action}
      className={clx(
        "dashboard-card recent-activity",
        showCompany && "recent-activity--with-company",
      )}
    >
      {entries.length > 0 ? (
        <ul
          className="recent-activity__list"
          style={{ "--recent-activity-visible-rows": visibleRows }}
        >
          {entries.map((entry) => (
            <li key={entry.id} className="recent-activity__row">
              <span className="recent-activity__date">
                {formatActivityDateTime(entry.createdAt, lang)}
              </span>
              <span className="recent-activity__actor">
                <TruncatedWithTooltip title={entry.actor.name}>
                  <span className="recent-activity__name">
                    {entry.actor.name || "—"}
                  </span>
                </TruncatedWithTooltip>
                {entry.actor.role ? (
                  <span className="recent-activity__role">
                    {entry.actor.role}
                  </span>
                ) : null}
              </span>
              {showCompany ? (
                <TruncatedWithTooltip title={entry.company}>
                  {entry.companyId ? (
                    <Link
                      className="recent-activity__company recent-activity__company-link"
                      to={generatePath(ROUTE_PATHS.companiesListCompanyDetails, {
                        id: entry.companyId,
                      })}
                    >
                      {entry.company}
                    </Link>
                  ) : (
                    <span className="recent-activity__company">
                      {entry.company || "—"}
                    </span>
                  )}
                </TruncatedWithTooltip>
              ) : null}
              <TruncatedWithTooltip title={entry.action}>
                <span className="recent-activity__action">{entry.action}</span>
              </TruncatedWithTooltip>
              {entry.module ? (
                <UIChip
                  color={ACTIVITY_MODULE_COLOR[entry.module]}
                  label={modules[entry.module]}
                  className="recent-activity__tag"
                />
              ) : (
                <span className="recent-activity__tag">—</span>
              )}
            </li>
          ))}
        </ul>
      ) : (
        <DashboardEmptyState
          icon={<History />}
          title={emptyTitle}
          description={emptyDescription}
        />
      )}
    </UICard>
  );
};

RecentActivityLog.propTypes = {
  title: PropTypes.string.isRequired,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      /** Undefined for a `null` / unknown API module — shows a dash. */
      module: PropTypes.oneOf(Object.keys(ACTIVITY_MODULE_COLOR)),
      actor: PropTypes.shape({
        name: PropTypes.string.isRequired,
        role: PropTypes.string,
      }).isRequired,
      /** The company the entry belongs to (shown with `showCompany`). */
      company: PropTypes.string,
      /** Links the company name to its profile; `null` with `company`. */
      companyId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      /** Localised action text. */
      action: PropTypes.string.isRequired,
      createdAt: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
        .isRequired,
    }),
  ),
  modules: PropTypes.objectOf(PropTypes.string).isRequired,
  lang: PropTypes.string.isRequired,
  /** Most entries shown. */
  limit: PropTypes.number,
  /** Rows visible before the list scrolls. */
  visibleRows: PropTypes.number,
  /** Adds the Company column (admin portal). */
  showCompany: PropTypes.bool,
  fullLogsLabel: PropTypes.string,
  onFullLogs: PropTypes.func,
  emptyTitle: PropTypes.string.isRequired,
  emptyDescription: PropTypes.string.isRequired,
};

RecentActivityLog.defaultProps = {
  items: [],
  limit: RECENT_ACTIVITY_LIMIT,
  visibleRows: 4,
  showCompany: false,
  fullLogsLabel: undefined,
  onFullLogs: undefined,
};

export default RecentActivityLog;
